import {
  DevicesDeviceCommand,
  DevicesPlayState,
  useExecuteCommand,
  type DevicesDevice,
  type DevicesDeviceState,
} from '@smarthome/api';
import { useDebounce } from '@smarthome/ui';
import {
  AlertCircle,
  Minus,
  Pause,
  Play,
  Plus,
  SkipBack,
  SkipForward,
  Square,
  VolumeX,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { byte255ToPercent, getDeviceIdForApi, getDeviceOn, percentToByte255 } from '../../utils';

import { Badge, Select, Slider, Toggle } from '@/components/ui';

export interface SpeakerControlsProps {
  device: DevicesDevice;
  onUpdateState: (state: DevicesDeviceState) => void;
  disabled?: boolean;
}

export function SpeakerControls({ device, onUpdateState, disabled }: SpeakerControlsProps) {
  const capabilities = device.capabilities?.speaker;
  const speakerState = device.state?.speaker;
  const isOn = getDeviceOn(device);
  const deviceId = getDeviceIdForApi(device);

  // Command error state
  const [commandError, setCommandError] = useState<string | null>(null);

  // Local state for volume slider (speaker volume is 0-255)
  const rawVolume = speakerState?.volume ?? 0;
  const [localVolume, setLocalVolume] = useState(() => byte255ToPercent(rawVolume));
  const debouncedVolume = useDebounce(localVolume, 300);

  // Track if we initiated the change
  const isUserChange = useRef(false);
  const lastSentVolume = useRef(debouncedVolume);

  // Sync local state with device state
  useEffect(() => {
    if (!isUserChange.current) {
      setLocalVolume(byte255ToPercent(rawVolume));
    }
  }, [rawVolume]);

  // Send debounced volume updates
  useEffect(() => {
    if (isUserChange.current && debouncedVolume !== lastSentVolume.current) {
      lastSentVolume.current = debouncedVolume;
      onUpdateState({ speaker: { volume: percentToByte255(debouncedVolume) } });
      isUserChange.current = false;
    }
  }, [debouncedVolume, onUpdateState]);

  // Execute command hook
  const { mutate: executeCommand } = useExecuteCommand({
    mutation: {
      onError: () => {
        setCommandError('Command failed. This device may not support this action.');
        setTimeout(() => setCommandError(null), 3000);
      },
    },
  });

  const handleCommand = useCallback(
    (command: DevicesDeviceCommand) => {
      if (!deviceId) return;
      setCommandError(null);
      executeCommand({ id: deviceId, data: { command } });
    },
    [deviceId, executeCommand]
  );

  const handlePowerToggle = (checked: boolean) => {
    onUpdateState({ speaker: { on: checked } });
  };

  const handleVolumeChange = (value: number) => {
    isUserChange.current = true;
    setLocalVolume(value);
  };

  const handleSourceChange = (sourceId: string) => {
    onUpdateState({ speaker: { source: sourceId } });
  };

  // Source options - filter out sources without an ID
  const sourceOptions = useMemo(
    () =>
      (capabilities?.sources ?? [])
        .filter((src) => src.id)
        .map((src) => ({
          value: src.id!,
          label: src.label ?? src.id ?? 'Unknown',
        })),
    [capabilities?.sources]
  );

  // Determine which controls to show
  const showVolumeIncrement = capabilities?.volumeIncrement === true;
  const showMuteControl = capabilities?.mute === true;
  const showSourceSelect =
    capabilities?.sourceSelect === true && (capabilities?.sources?.length ?? 0) > 0;
  const showPlayControl = capabilities?.playControl === true;
  const showTrackControl = capabilities?.trackControl === true;

  // Play state badge config
  const getPlayStateBadge = () => {
    switch (speakerState?.playState) {
      case DevicesPlayState.PlayStatePlaying:
        return { variant: 'success' as const, label: 'Playing' };
      case DevicesPlayState.PlayStatePaused:
        return { variant: 'warning' as const, label: 'Paused' };
      case DevicesPlayState.PlayStateStopped:
        return { variant: 'default' as const, label: 'Stopped' };
      default:
        return null;
    }
  };

  const playStateBadge = getPlayStateBadge();

  return (
    <div className="-m-4 space-y-4 p-4">
      {/* Command error */}
      {commandError && (
        <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{commandError}</span>
        </div>
      )}

      {/* Power Card */}
      <div className="rounded-xl bg-white p-1 ring-1 ring-gray-200 dark:bg-zinc-900 dark:ring-white/[0.06]">
        <div className="flex items-center justify-between rounded-lg px-3 py-2.5 transition-colors hover:bg-gray-50 dark:hover:bg-white/[0.02]">
          <div className="flex-1">
            <Toggle
              label="Power"
              layout="switch-right"
              checked={isOn ?? false}
              onChange={handlePowerToggle}
              disabled={disabled}
            />
          </div>
          {playStateBadge && (
            <Badge variant={playStateBadge.variant} size="sm" dot className="ml-3">
              {playStateBadge.label}
            </Badge>
          )}
        </div>
      </div>

      {/* Volume Card - Integrated slider with optional +/- and mute */}
      <div className="rounded-xl bg-white p-4 ring-1 ring-gray-200 dark:bg-zinc-900 dark:ring-white/[0.06]">
        <div className="mb-3 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700 dark:text-zinc-300">Volume</span>
          <span className="text-sm text-gray-500 dark:text-zinc-500">{localVolume}%</span>
        </div>
        {/* Integrated slider with optional +/- buttons */}
        <div className="flex items-center gap-3">
          {showVolumeIncrement && (
            <button
              type="button"
              onClick={() => handleCommand(DevicesDeviceCommand.DeviceCommandVolumeDown)}
              disabled={disabled}
              className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700"
              aria-label="Volume down"
            >
              <Minus className="h-4 w-4" />
            </button>
          )}
          <Slider
            label="Volume"
            hideLabel
            min={0}
            max={100}
            value={localVolume}
            onChange={handleVolumeChange}
            showValue={false}
            disabled={disabled}
            className="flex-1"
          />
          {showVolumeIncrement && (
            <button
              type="button"
              onClick={() => handleCommand(DevicesDeviceCommand.DeviceCommandVolumeUp)}
              disabled={disabled}
              className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700"
              aria-label="Volume up"
            >
              <Plus className="h-4 w-4" />
            </button>
          )}
          {showMuteControl && (
            <button
              type="button"
              onClick={() => handleCommand(DevicesDeviceCommand.DeviceCommandToggleMute)}
              disabled={disabled}
              className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700"
              aria-label="Mute"
            >
              <VolumeX className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Source Card */}
      {showSourceSelect && (
        <div className="rounded-xl bg-white p-4 ring-1 ring-gray-200 dark:bg-zinc-900 dark:ring-white/[0.06]">
          <Select
            label="Source"
            value={speakerState?.source ?? ''}
            onChange={handleSourceChange}
            options={sourceOptions}
            placeholder="Select source..."
            disabled={disabled}
            fullWidth
          />
        </div>
      )}

      {/* Playback Card */}
      {(showPlayControl || showTrackControl) && (
        <div className="rounded-xl bg-white p-4 ring-1 ring-gray-200 dark:bg-zinc-900 dark:ring-white/[0.06]">
          <label className="mb-3 block text-sm font-medium text-gray-700 dark:text-zinc-300">
            Playback
          </label>
          <div className="flex items-center justify-center gap-3">
            {showTrackControl && (
              <button
                type="button"
                onClick={() => handleCommand(DevicesDeviceCommand.DeviceCommandPrevious)}
                disabled={disabled}
                className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700"
                aria-label="Previous track"
              >
                <SkipBack className="h-4 w-4" />
              </button>
            )}
            {showPlayControl && (
              <>
                <button
                  type="button"
                  onClick={() => handleCommand(DevicesDeviceCommand.DeviceCommandPlayPause)}
                  disabled={disabled}
                  className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-500 text-white transition-colors hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label={
                    speakerState?.playState === DevicesPlayState.PlayStatePlaying ? 'Pause' : 'Play'
                  }
                >
                  {speakerState?.playState === DevicesPlayState.PlayStatePlaying ? (
                    <Pause className="h-5 w-5" />
                  ) : (
                    <Play className="h-5 w-5" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => handleCommand(DevicesDeviceCommand.DeviceCommandStop)}
                  disabled={disabled}
                  className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700"
                  aria-label="Stop"
                >
                  <Square className="h-4 w-4" />
                </button>
              </>
            )}
            {showTrackControl && (
              <button
                type="button"
                onClick={() => handleCommand(DevicesDeviceCommand.DeviceCommandNext)}
                disabled={disabled}
                className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700"
                aria-label="Next track"
              >
                <SkipForward className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
